import { useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Play,
  X,
  XCircle,
} from 'lucide-react';
import {
  clearStep5Intent,
  fetchStep5Status,
  startPerfReport,
} from '../../redux/slice/step5Slice';
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../redux/store';
import { PerfReportFactorStatus } from '../../types/step5';
// When the report is COMPLETED we delegate to the legacy ViewReport component, which
// renders the rich per-factor accordion + per-query red/yellow/green table that pre-dates
// the Step 5 split. It reads from the same query-report S3 prefix the new MODE=perf_report
// container writes to, so the existing component continues to work unchanged.
import ViewReport from '../viewReport/viewReport';
import RdsLockQueuedBanner from '../rdsLock/RdsLockQueuedBanner';

// Same cadence as the replay poll — they share an endpoint anyway, so the cost is one
// extra request per tick which is cheap (and lets each tab poll independently while the
// other is unmounted).
const POLL_INTERVAL_MS = 30_000;

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '\u2014';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function StatusBadge({
  status,
}: {
  status: PerfReportFactorStatus['status'] | 'QUEUED' | 'REJECTED' | null;
}) {
  switch (status) {
    case 'PENDING':
    case 'QUEUED':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-gray-500 rounded-full animate-pulse" />
          {status}
        </div>
      );
    case 'RUNNING':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
          <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
          RUNNING
        </div>
      );
    case 'COMPLETED':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          COMPLETED
        </div>
      );
    case 'FAILED':
    case 'REJECTED':
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
          <XCircle className="w-4 h-4" />
          {status}
        </div>
      );
    default:
      return null;
  }
}

function PerformanceReport() {
  const dispatch = useAppDispatch();

  const projectId = useAppSelector(
    (state: RootState) => state.userProject.currentProject.project_id
  );
  const runId = useAppSelector(
    (state: RootState) => state.userProject.currentRunId
  );

  const step5 = useAppSelector((state: RootState) => state.step5);
  const {
    status,
    isLoading,
    loadError,
    isTriggeringPerfReport,
    triggerPerfReportError,
    isCancellingIntent,
    cancelIntentError,
  } = step5;

  // Mount fetch + clean up on scope change.
  useEffect(() => {
    if (projectId > 0 && runId != null && runId > 0) {
      dispatch(fetchStep5Status(projectId, runId));
    }
  }, [dispatch, projectId, runId]);

  // Poll while anything is non-terminal (scaling RUNNING, perf RUNNING/PENDING, intent
  // QUEUED). We stop polling once everything reaches a terminal state to avoid leaks.
  useEffect(() => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    const shouldPoll =
      !status ||
      status.scaling?.status === 'PENDING' ||
      status.scaling?.status === 'RUNNING' ||
      status.scaling?.status === 'NOT_STARTED' ||
      status.perf_report?.status === 'PENDING' ||
      status.perf_report?.status === 'RUNNING' ||
      status.replay?.status === 'PENDING' ||
      status.replay?.status === 'RUNNING' ||
      status.intent?.perf_report?.status === 'QUEUED' ||
      status.intent?.replay?.status === 'QUEUED' ||
      status.rds_lock?.status === 'QUEUED';
    if (!shouldPoll) return;

    const intervalId = window.setInterval(() => {
      dispatch(fetchStep5Status(projectId, runId, { silent: true }));
    }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, projectId, runId, status]);

  const handleTrigger = () => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    dispatch(startPerfReport(projectId, runId));
  };

  const handleCancelQueue = () => {
    if (projectId <= 0 || runId == null || runId <= 0) return;
    dispatch(clearStep5Intent(projectId, runId, 'perf_report'));
  };

  const scalingStatus = status?.scaling?.status;
  const intentEntry = status?.intent?.perf_report;
  const perfReport = status?.perf_report;

  const hasActivePerfReport = useMemo(() => {
    if (!perfReport) return false;
    return (
      perfReport.status === 'PENDING' ||
      perfReport.status === 'RUNNING' ||
      perfReport.status === 'COMPLETED' ||
      perfReport.status === 'FAILED'
    );
  }, [perfReport]);

  // ----- Loading guard (initial mount only; silent polls keep the UI stable) -----
  if (isLoading && status === null) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <FormattedMessage id="steps.5.perfReport.loading" />
          </div>
        </div>
      </div>
    );
  }

  // ----- Rejected (scaling or sibling step failed for this run) -----
  if (intentEntry?.status === 'REJECTED') {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.perfReport.heading" />
            </h2>
            <StatusBadge status="REJECTED" />
          </div>
          <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-red-800">
              <span className="font-medium">
                <FormattedMessage id="steps.5.perfReport.rejectedLabel" />:{' '}
              </span>
              {intentEntry.rejected_reason ||
                'A prerequisite step failed; please create a new run.'}
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCancelQueue}
              disabled={isCancellingIntent === 'perf_report'}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <FormattedMessage id="steps.5.perfReport.dismissButton" />
            </button>
          </div>
          {cancelIntentError && (
            <p className="text-sm text-red-700 mt-2">{cancelIntentError}</p>
          )}
        </div>
      </div>
    );
  }

  // ----- Queued (scaling or sibling step still running) -----
  if (intentEntry?.status === 'QUEUED' && !hasActivePerfReport) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <RdsLockQueuedBanner rdsLock={status?.rds_lock} />
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.perfReport.heading" />
            </h2>
            <StatusBadge status="QUEUED" />
          </div>
          <div className="bg-blue-50 border border-solid border-blue-200 rounded-lg p-4 mb-4 flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-900">
              {intentEntry.queued_reason ||
                'Your performance report is queued and will run automatically.'}
            </p>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCancelQueue}
              disabled={isCancellingIntent === 'perf_report'}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <FormattedMessage id="steps.5.perfReport.cancelQueueButton" />
            </button>
          </div>
          {cancelIntentError && (
            <p className="text-sm text-red-700 mt-2">{cancelIntentError}</p>
          )}
        </div>
      </div>
    );
  }

  // ----- Idle (no perf report rows, no intent) — show Trigger CTA -----
  if (!hasActivePerfReport) {
    const scalingNotDone =
      scalingStatus === 'PENDING' ||
      scalingStatus === 'RUNNING' ||
      scalingStatus === 'NOT_STARTED';
    const scalingFailed = scalingStatus === 'FAILED';

    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b] mb-2">
              <FormattedMessage id="steps.5.perfReport.heading" />
            </h2>
            <p className="text-gray-600 text-sm">
              <FormattedMessage id="steps.5.perfReport.idleDescription" />
            </p>
          </div>

          {scalingNotDone &&
            (scalingStatus === 'PENDING' || scalingStatus === 'RUNNING') && (
              <div className="bg-blue-50 border border-solid border-blue-200 rounded-lg p-3 mb-4 text-sm text-blue-900">
                <FormattedMessage id="steps.5.perfReport.scalingEmailHint" />
              </div>
            )}

          {scalingFailed && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-800">
              <FormattedMessage id="steps.5.perfReport.scalingFailedNotice" />
            </div>
          )}

          <div className="bg-yellow-50 border border-solid border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">
                  <FormattedMessage id="steps.5.perfReport.notice.title" />
                </p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>
                    <FormattedMessage id="steps.5.perfReport.notice.bullet1" />
                  </li>
                  <li>
                    <FormattedMessage id="steps.5.perfReport.notice.bullet2" />
                  </li>
                  <li>
                    <FormattedMessage id="steps.5.perfReport.notice.bullet3" />
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {triggerPerfReportError && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {triggerPerfReportError}
            </div>
          )}

          {loadError && !triggerPerfReportError && (
            <div className="bg-red-50 border border-solid border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
              {loadError}
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={handleTrigger}
              disabled={isTriggeringPerfReport || scalingFailed}
              className="flex items-center gap-2 bg-[#1e1b4b] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              {isTriggeringPerfReport ? (
                <FormattedMessage id="steps.5.perfReport.startingButton" />
              ) : scalingNotDone ? (
                <FormattedMessage id="steps.5.perfReport.queueButton" />
              ) : (
                <FormattedMessage id="steps.5.perfReport.startButton" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ----- Running / Pending — per-factor status table -----
  if (
    perfReport &&
    (perfReport.status === 'PENDING' || perfReport.status === 'RUNNING')
  ) {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.perfReport.heading" />
            </h2>
            <StatusBadge status={perfReport.status} />
          </div>

          <div className="border border-solid border-gray-200 rounded-lg overflow-hidden mb-4">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.factor" />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.status" />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.startedAt" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {perfReport.factors.map((factor) => (
                  <tr key={factor.factor || 'unknown'}>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {factor.factor}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={factor.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatDateTime(factor.started_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-gray-50 border border-solid border-gray-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600">
              <FormattedMessage id="steps.5.perfReport.runningHint" />
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <FormattedMessage id="steps.5.description2" />
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ----- Failed (at least one factor FAILED) -----
  if (perfReport && perfReport.status === 'FAILED') {
    return (
      <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
        <div className="bg-white rounded-lg shadow-sm border border-solid border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#1e1b4b]">
              <FormattedMessage id="steps.5.perfReport.heading" />
            </h2>
            <StatusBadge status="FAILED" />
          </div>
          <div className="border border-solid border-gray-200 rounded-lg overflow-hidden mb-4">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.factor" />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.status" />
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">
                    <FormattedMessage id="steps.5.perfReport.col.error" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {perfReport.factors.map((factor) => (
                  <tr key={factor.factor || 'unknown'}>
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                      {factor.factor}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <StatusBadge status={factor.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-red-700 break-words">
                      {factor.error_message || '\u2014'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-gray-600">
            <FormattedMessage id="steps.5.perfReport.failedHint" />
          </p>
        </div>
      </div>
    );
  }

  // ----- Completed — defer to the original ViewReport (accordion + per-query tables +
  // red/yellow/green filter). That component reads from the query-report endpoints which
  // are still wired up; the new MODE=perf_report container writes to the same S3 prefix
  // the previous combined Step 4 flow used, so there's nothing to change on the data
  // side. We keep the status badge above the legacy view for visual continuity with the
  // other Step 5 cards.
  return (
    <div className="bg-[#f3f4f6] -mx-4 -mt-4 px-4 py-6 min-h-full font-sans">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-[#1e1b4b]">
          <FormattedMessage id="steps.5.perfReport.heading" />
        </h2>
        <StatusBadge status="COMPLETED" />
      </div>
      <ViewReport />
    </div>
  );
}

export default PerformanceReport;
